import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Briefcase, Users, ChevronRight, Trash2, AlertTriangle, UserCircle2, Plus, ArrowRight, CheckCircle2, Circle, Layers } from "lucide-react";
import { FALLBACK_CATEGORIES, getCategoryIcon } from "../lib/categories";
import CategoryBadge from "../components/CategoryBadge";
import CompanyBillingBanner from "../components/CompanyBillingBanner";
import useCompanyGated from "../hooks/useCompanyGated";

const STATUS_STYLES = {
  active:  "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30",
  draft:   "bg-secondary text-muted-foreground border-border",
  paused:  "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30",
  closed:  "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30",
};

// ── Small inline stat chip for the header ──────────────────────
function StatChip({ value, label, accent }) {
  return (
    <div className={`inline-flex items-baseline gap-1.5 rounded-full border px-3 py-1 ${accent ? "border-[#0025D8]/25 bg-[#0025D8]/5" : "border-border bg-white"}`}>
      <span className={`font-display font-bold text-sm ${accent ? "text-[#0025D8]" : "text-[#0F172A]"}`}>{value}</span>
      <span className="text-[11px] text-muted-foreground">{label}</span>
    </div>
  );
}

// ── Redesigned role card ───────────────────────────────────────
function RoleCard({ role, onDelete, isAdmin, currentUserId }) {
  const canDelete = isAdmin || role.company_user_id === currentUserId;
  return (
    <Link to={`/company/roles/${role.company_role_id}`} className="block group">
      <div className="rounded-2xl border border-border bg-white p-6 hover:border-[#0025D8]/30 hover:shadow-lg hover:shadow-[#0F172A]/5 hover:-translate-y-0.5 transition-all">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <CategoryBadge category={role.category} />
            {role.employment_type && (
              <span className="font-mono text-[10px] px-2 py-0.5 rounded-md border border-border text-muted-foreground">
                {role.employment_type}
              </span>
            )}
            <span className={`font-mono text-[10px] px-2 py-0.5 rounded-md border font-semibold uppercase tracking-[0.1em] ${STATUS_STYLES[role.status] || STATUS_STYLES.active}`}>
              {role.status}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {canDelete && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(role); }}
                className="p-1.5 text-muted-foreground hover:text-[#E11D48] hover:bg-[#E11D48]/5 transition-colors rounded-lg"
                title="Delete role">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[#0025D8] group-hover:translate-x-0.5 transition-all" />
          </div>
        </div>

        <h3 className="font-display font-bold text-xl mb-1 text-[#0F172A] group-hover:text-[#0025D8] transition-colors">{role.title}</h3>
        {role.end_client && (
          <p className="font-mono text-[10px] text-muted-foreground tracking-[0.1em] mb-0.5">End client: {role.end_client}</p>
        )}
        {role.location && (
          <p className="font-mono text-[10px] text-muted-foreground tracking-[0.1em] mb-2">{role.location}</p>
        )}
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{role.description}</p>

        {role.required_skills?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {role.required_skills.slice(0, 5).map((s) => (
              <span key={s} className="font-mono text-[9px] px-2 py-0.5 rounded-md bg-secondary border border-border text-muted-foreground">{s}</span>
            ))}
            {role.required_skills.length > 5 && (
              <span className="font-mono text-[9px] px-1.5 py-0.5 text-muted-foreground">+{role.required_skills.length - 5} more</span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Users className="w-3.5 h-3.5" />
              <span className="font-mono">{role.candidate_count} candidate{role.candidate_count !== 1 ? "s" : ""}</span>
            </div>
            <span className="font-mono text-[10px] text-muted-foreground">
              {new Date(role.created_at).toLocaleDateString()}
            </span>
          </div>
          {isAdmin && role.created_by_name && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <UserCircle2 className="w-3 h-3 flex-shrink-0" />
              <span className="font-mono text-[9px] truncate max-w-[100px]">{role.created_by_name}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function CompanyRoles() {
  const { user } = useAuth();
  const isAdmin = user?.role === "company" && user?.sub_role !== "employee";
  const { isLocked } = useCompanyGated();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeCreator, setActiveCreator] = useState("All");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [categories, setCategories] = useState(["All", ...FALLBACK_CATEGORIES]);

  useEffect(() => {
    api.get("/company/industries").then(({ data }) => {
      if (data?.length) setCategories(["All", ...data]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const params = activeCategory !== "All" ? `?category=${encodeURIComponent(activeCategory)}` : "";
        const { data } = await api.get(`/company/roles/mine${params}`);
        setRoles(data);
      } finally { setLoading(false); }
    })();
  }, [activeCategory]);

  // Build creator list with role counts (from unfiltered roles, for stable counts)
  const creatorCounts = roles.reduce((acc, r) => {
    if (r.created_by_name) acc[r.created_by_name] = (acc[r.created_by_name] || 0) + 1;
    return acc;
  }, {});
  const creators = Object.entries(creatorCounts).sort((a, b) => b[1] - a[1]);
  const showCreatorFilter = isAdmin && creators.length > 1;

  const visibleRoles = activeCreator === "All"
    ? roles
    : roles.filter((r) => r.created_by_name === activeCreator);

  const pipelineRoles = [...roles].filter((r) => r.candidate_count > 0).sort((a, b) => b.candidate_count - a.candidate_count);
  const totalCandidates = roles.reduce((s, r) => s + r.candidate_count, 0);
  const activeCount = roles.filter((r) => r.status === "active").length;

  const handleCategoryChange = (cat) => {
    setLoading(true);
    setActiveCategory(cat);
    setActiveCreator("All");
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/company/roles/${deleteTarget.company_role_id}`);
      setRoles((prev) => prev.filter((r) => r.company_role_id !== deleteTarget.company_role_id));
      toast.success(`"${deleteTarget.title}" deleted`);
      setDeleteTarget(null);
    } catch { toast.error("Could not delete role — try again"); }
    finally { setDeleting(false); }
  };

  const hasRoles = !loading && roles.length > 0;

  return (
    <>
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">

      <CompanyBillingBanner />

      {/* ══════════ HEADER ══════════ */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="font-mono text-[10px] text-[#0025D8] tracking-[0.25em] mb-2">HIRING CONTROL</div>
          <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A]">{user.name}</h1>
          <p className="text-muted-foreground text-sm mt-1.5">Manage your open roles and candidate pipelines.</p>

          {hasRoles && (
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <StatChip value={roles.length} label={`role${roles.length !== 1 ? "s" : ""}`} />
              <StatChip value={activeCount} label="active" />
              <StatChip value={totalCandidates} label="candidates" accent />
              <StatChip
                value={isAdmin ? creators.length : new Set(roles.map((r) => r.category)).size}
                label={isAdmin ? `team member${creators.length !== 1 ? "s" : ""}` : "categories"}
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isAdmin && (
            <Link to="/company/employees"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-semibold text-[#0F172A] hover:bg-secondary transition-colors">
              <UserCircle2 className="w-4 h-4" /> Team
            </Link>
          )}
          {isAdmin && (
            <Link to="/company/integrations"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-semibold text-[#0F172A] hover:bg-secondary transition-colors">
              <Layers className="w-4 h-4" /> Integrations
            </Link>
          )}
          {isLocked ? (
            <span
              title="Activate your account to post roles"
              className="inline-flex items-center gap-2 rounded-xl bg-secondary text-muted-foreground px-5 py-2.5 text-sm font-semibold cursor-not-allowed opacity-60 select-none">
              <Plus className="w-4 h-4" /> Post a role
            </span>
          ) : (
            <Link to="/company/roles/new"
              className="inline-flex items-center gap-2 rounded-xl bg-[#0025D8] hover:bg-[#001CB3] text-white px-5 py-2.5 text-sm font-semibold shadow-lg shadow-[#0025D8]/20 transition-all">
              <Plus className="w-4 h-4" /> Post a role
            </Link>
          )}
        </div>
      </div>

      {/* ══════════ ACTIVE PIPELINES (lead with the work) ══════════ */}
      {hasRoles && pipelineRoles.length > 0 && (
        <section className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em]">ACTIVE PIPELINES · NEEDS REVIEW</div>
            <span className="font-mono text-[10px] text-muted-foreground">{totalCandidates} candidate{totalCandidates !== 1 ? "s" : ""} total</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {pipelineRoles.slice(0, 6).map((role) => (
              <Link key={role.company_role_id} to={`/company/roles/${role.company_role_id}`}
                className="group flex items-center justify-between rounded-xl border border-[#0025D8]/15 bg-[#0025D8]/[0.03] p-4 hover:border-[#0025D8]/40 hover:bg-[#0025D8]/5 transition-all">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-[#0F172A] truncate">{role.title}</div>
                  <div className="flex items-center gap-1.5 mt-0.5 text-[#0025D8]">
                    <Users className="w-3 h-3" />
                    <span className="font-mono text-[11px] font-semibold">{role.candidate_count} candidate{role.candidate_count !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[#0025D8] group-hover:translate-x-0.5 flex-shrink-0 ml-2 transition-all" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ══════════ FILTER BAR ══════════ */}
      {hasRoles && (
        <div className="mb-6">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mr-1">CATEGORY</span>
            {categories.map((cat) => {
              const Icon = cat === "All" ? null : getCategoryIcon(cat);
              return (
                <button key={cat} onClick={() => handleCategoryChange(cat)}
                  className={`inline-flex items-center gap-1.5 font-mono text-[10px] px-3 py-1.5 rounded-full border transition-all duration-150 ${
                    activeCategory === cat
                      ? "bg-[#0025D8] text-white border-[#0025D8]"
                      : "text-muted-foreground border-border bg-white hover:border-[#0025D8] hover:text-[#0025D8]"
                  }`}>
                  {Icon && <Icon className="w-3 h-3 flex-shrink-0" />}
                  {cat}
                </button>
              );
            })}
          </div>

          {showCreatorFilter && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mr-1">CREATED BY</span>
              <button onClick={() => setActiveCreator("All")}
                className={`font-mono text-[10px] px-3 py-1.5 rounded-full border transition-all duration-150 ${
                  activeCreator === "All"
                    ? "bg-[#0025D8] text-white border-[#0025D8]"
                    : "text-muted-foreground border-border bg-white hover:border-[#0025D8] hover:text-[#0025D8]"
                }`}>
                All ({roles.length})
              </button>
              {creators.map(([name, count]) => (
                <button key={name} onClick={() => setActiveCreator(name)}
                  className={`font-mono text-[10px] px-3 py-1.5 rounded-full border transition-all duration-150 flex items-center gap-1.5 ${
                    activeCreator === name
                      ? "bg-[#0025D8] text-white border-[#0025D8]"
                      : "text-muted-foreground border-border bg-white hover:border-[#0025D8] hover:text-[#0025D8]"
                  }`}>
                  <UserCircle2 className="w-2.5 h-2.5" />
                  {name} ({count})
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════ ROLES / EMPTY STATES ══════════ */}
      {loading ? (
        <div className="p-12 font-mono text-sm text-muted-foreground text-center">Loading roles…</div>
      ) : visibleRoles.length === 0 ? (
        activeCreator !== "All" || activeCategory !== "All" ? (
          /* Filter-based empty state */
          <div className="rounded-2xl border border-dashed border-border p-16 text-center">
            <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="font-semibold text-[#0F172A] mb-1">
              {activeCreator !== "All"
                ? `No roles by ${activeCreator}${activeCategory !== "All" ? ` in ${activeCategory}` : ""}.`
                : `No ${activeCategory} roles found.`}
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              {activeCreator !== "All" ? "Try a different employee or clear the filter." : "Try a different category or post a new role."}
            </p>
            {activeCreator !== "All" ? (
              <button onClick={() => setActiveCreator("All")} className="font-mono text-sm text-[#0025D8] underline">Clear filter</button>
            ) : (
              <Link to="/company/roles/new"
                className="inline-flex items-center gap-2 rounded-xl bg-[#0025D8] hover:bg-[#001CB3] text-white px-5 py-2.5 text-sm font-semibold shadow-lg shadow-[#0025D8]/20 transition-all">
                <Plus className="w-4 h-4" /> Post a role
              </Link>
            )}
          </div>
        ) : (
          /* First-time onboarding — no roles at all */
          <div className="rounded-2xl border border-border overflow-hidden bg-white">
            <div className="p-10 text-center border-b border-border bg-secondary/30">
              <div className="w-12 h-12 rounded-xl bg-[#0025D8]/10 flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-6 h-6 text-[#0025D8]" />
              </div>
              <h2 className="font-display font-bold text-2xl mb-2 text-[#0F172A]">Welcome to your hiring dashboard</h2>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">Get your first AI-powered interview up and running in 3 steps.</p>
            </div>
            <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
              {[
                { step: "01", icon: <Plus className="w-5 h-5 text-[#0025D8]" />, iconBg: "bg-[#0025D8]/10", title: "Post a role", desc: "Define the position, requirements, and job description for the role you're hiring.", action: { label: "Post your first role", href: "/company/roles/new" } },
                { step: "02", icon: <CheckCircle2 className="w-5 h-5 text-muted-foreground" />, iconBg: "bg-secondary", title: "Set up your interview", desc: "Build your question bank and configure the AI interviewer for the role.", action: null },
                { step: "03", icon: <Users className="w-5 h-5 text-muted-foreground" />, iconBg: "bg-secondary", title: "Invite candidates", desc: "Send AI interview links to shortlisted candidates and track their responses.", action: null },
              ].map(({ step, icon, iconBg, title, desc, action }) => (
                <div key={step} className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>{icon}</div>
                    <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em]">STEP {step}</div>
                  </div>
                  <h3 className="font-display font-bold text-lg mb-2 text-[#0F172A]">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6">{desc}</p>
                  {action ? (
                    <Link to={action.href}
                      className="inline-flex items-center gap-2 rounded-xl bg-[#0025D8] hover:bg-[#001CB3] text-white px-4 py-2.5 text-sm font-semibold shadow-lg shadow-[#0025D8]/20 transition-all">
                      {action.label} <ArrowRight className="w-4 h-4" />
                    </Link>
                  ) : (
                    <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
                      <Circle className="w-3 h-3" /> Complete the previous step first
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      ) : (
        <>
          <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-3">ALL ROLES</div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {visibleRoles.map((role) => <RoleCard key={role.company_role_id} role={role} onDelete={setDeleteTarget} isAdmin={isAdmin} currentUserId={user?.user_id} />)}
          </div>
        </>
      )}
    </main>

    {/* ══════════ DELETE CONFIRMATION ══════════ */}
    {deleteTarget && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl border border-border max-w-sm w-full p-6 shadow-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-[#E11D48]/10 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4.5 h-4.5 text-[#E11D48]" />
            </div>
            <div>
              <p className="font-semibold text-sm text-[#0F172A]">Delete this role?</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                <strong>{deleteTarget.title}</strong> and all its candidates, interview setup, and invites will be permanently removed.
              </p>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setDeleteTarget(null)} disabled={deleting}
              className="px-4 py-2 rounded-xl text-sm border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
              Cancel
            </button>
            <button onClick={confirmDelete} disabled={deleting}
              className="px-4 py-2 rounded-xl text-sm font-bold bg-[#E11D48] text-white hover:bg-[#be1238] transition-colors disabled:opacity-60">
              {deleting ? "Deleting…" : "Delete Role"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
